import { getQueryArgs } from "@js/nwcc-navbar.js";
import { loadMainNwcc, setArgDetailsData } from "@js/nwcc-template.js";
import { getLuts } from "../model/get-data.js";
import { splitCamel } from "@js/nwcc-utils.js";

let luts = {};

function getPageArgs(lutChoices) {
    return [
        {
            argName: "table",
            argDesc: "Select and show the specified table.",
            argExample: "instruments",
            argOptions: lutChoices,
        },
        {
            argName: "visibility",
            argDesc: "Select and show the specified table.",
            argOptions: ["all", "public"],
            argDefault: "all",
            argExample: "all",
            argInternal: true,
        },
    ];
}

export async function windowOnLoad() {
    const main = document.getElementById("main-content");
    main.style.visibility = "hidden";
    document.getElementById("download-btn").addEventListener("click", downloadLut);
    luts = await getLuts(getQueryArgs(), flagLut);
    populateLutsSelect();
    const pageArgs = getPageArgs(Object.keys(luts));
    setArgDetailsData(pageArgs);
    loadMainNwcc();
}

window.onload = windowOnLoad;

async function populateLutsSelect() {
    const lutTables = Object.keys(luts).sort((a, b) => a - b);
    const lutSelect = document.getElementById("lut-select");
    lutTables.forEach((lut) => {
        const option = document.createElement("option");
        option.value = lut;
        option.innerHTML = splitCamel(lut);
        lutSelect.appendChild(option);
    });
    lutSelect.addEventListener("change", makeLutTable, false);
    const queryArgs = await getQueryArgs();
    const requestedLut = queryArgs.table || "";
    if (lutTables.includes(requestedLut)) {
        lutSelect.value = requestedLut;
        await makeLutTable();
    }
}

async function makeLutTable() {
    const lutSelect = document.getElementById("lut-select");
    const lutTable = document.getElementById("lut-table");
    const lutTableBox = lutTable.parentElement;
    lutTableBox.classList.remove("fsa-hide");
    const selectedLut = lutSelect.options[lutSelect.selectedIndex];
    selectedLut?.value ? buildLut(lutTable, selectedLut) : lutTableBox.classList.add("fsa-hide");
}

function buildLut(lutTable, selectedLut) {
    lutTable.innerHTML = "";
    const caption = document.createElement("caption");
    caption.className = "sr-only";
    caption.innerHTML = `A table showing the reference table for ${selectedLut.innerHTML}`;
    lutTable.appendChild(caption);
    const tableData = luts[selectedLut.value];
    const tHead = document.createElement("thead");
    const headTr = document.createElement("tr");
    const columns = [...new Set(tableData.flatMap((obj) => Object.keys(obj)))];
    columns.forEach((col) => {
        const headTh = document.createElement("th");
        headTh.className = "fsa-text-align--center fsa-table__th--sticky";
        headTh.scope = "col";
        headTh.innerHTML = splitCamel(col);
        headTr.appendChild(headTh);
    });
    tHead.appendChild(headTr);
    lutTable.appendChild(tHead);
    const tBody = document.createElement("tbody");
    tableData.forEach((rowData) => {
        const tr = document.createElement("tr");
        columns.forEach((col) => {
            const td = document.createElement("td");
            const data = rowData[col] ? rowData[col] : "--";
            td.ariaLabel = splitCamel(col);
            td.className = "fsa-text-align--left";
            td.innerHTML = data;
            tr.appendChild(td);
        });
        tBody.appendChild(tr);
    });
    lutTable.appendChild(tBody);
}

export function downloadLut() {
    const lutSelect = document.getElementById("lut-select");
    const selectedLut = lutSelect.value;
    let jsonStr = "";
    let downloadName = "";
    if (selectedLut !== "") {
        jsonStr = JSON.stringify(luts[selectedLut], null, 2);
        downloadName = `awdb-ref-data-${selectedLut}`;
    } else {
        jsonStr = JSON.stringify(luts, null, 2);
        downloadName = `awdb-ref-data`;
    }
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(jsonStr);
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", downloadName + ".json");
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
}

const flagLut = [
    {
        code: "V",
        type: "QC",
        name: "Valid",
        description:
            "Data value is within the limits of the Storage delta/range profile or determined to be valid through human review.",
    },
    {
        code: "S",
        type: "QC",
        name: "Suspect",
        description: "Data value is outside the limits of the stored delta/range profile.",
    },
    {
        code: "E",
        type: "QC",
        name: "Edited",
        description: "Data value is revised to correct for signal noise on an otherwise functioning sensor.",
    },
    {
        code: "N",
        type: "QC",
        name: "No Profile Validation",
        description:
            "This data was not checked against a delta/range profile for the data value, or the profiles are inactive.",
    },
    {
        code: "K",
        type: "QC",
        name: "Internal Estimate",
        description:
            "Data value is revised using an estimate from other sensors or stations. Does not apply to data that are smoothed to adjust for minor sensor fluctuations which would receive the 'E' flag.",
    },
    {
        code: "C",
        type: "QC",
        name: "Correction",
        description:
            "Assigned when a different multiplier or equation is applied to the sensor voltage (e.g. correcting for wrong transducer size, or unbiased temperature data). Usually applied outside DMP during temperature unbias or storage recalculate.",
    },
    {
        code: "M",
        type: "QC",
        name: "Missing",
        description: "Assigned for to a null value at any stage in the review process.",
    },
    {
        code: "R",
        type: "QA",
        name: "Raw data",
        description: "Has not received human review. Upon storage, all data will receive an R flag.",
    },
    {
        code: "P",
        type: "QA",
        name: "Provisional",
        description: "Has received initial human review and is provisionally approved. Subject to revision.",
    },
    {
        code: "A",
        type: "QA",
        name: "Approved",
        description:
            "Approved for publication. Processing and review completed. Can still be revised. Based on the standard procedures for finalizing SNOTEL data, the A flag should be applied at the completion of water year reviews.",
    },
];
